import type { CSSProperties } from "react";
import type { Icon as PhosphorIcon, IconWeight } from "@phosphor-icons/react";
import {
  AirplaneTilt,
  ArrowClockwise,
  ArrowCounterClockwise,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  ArrowsIn,
  ArrowsOut,
  Bell,
  BellSlash,
  BookmarkSimple,
  Buildings,
  CalendarBlank,
  CalendarPlus,
  Camera,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  ChatCircle,
  Check,
  CheckCircle,
  City,
  Clock,
  Compass,
  Confetti,
  Copy,
  Crop,
  Crosshair,
  Cube,
  Diamond,
  DotsSixVertical,
  DotsThree,
  DownloadSimple,
  Envelope,
  FilePdf,
  Fire,
  Flag,
  FolderOpen,
  FolderSimple,
  FolderSimplePlus,
  Funnel,
  Gear,
  GlobeSimple,
  HandGrabbing,
  HandHeart,
  HourglassSimple,
  House,
  Image,
  Info,
  Lightbulb,
  LinkSimple,
  ListChecks,
  LockSimple,
  MagnifyingGlass,
  MapPin,
  MapTrifold,
  Medal,
  Minus,
  Moon,
  Mountains,
  Newspaper,
  PaperPlaneTilt,
  PencilSimple,
  Phone,
  Play,
  Plus,
  Polygon,
  PushPin,
  PushPinSlash,
  RocketLaunch,
  Rss,
  SealCheck,
  SignIn,
  SignOut,
  SlidersHorizontal,
  Sparkle,
  SpinnerGap,
  SquaresFour,
  Stack,
  Star,
  Sun,
  Swap,
  Timer,
  Trash,
  Trophy,
  UploadSimple,
  User,
  UserCircle,
  Users,
  VideoCamera,
  Warning,
  WarningCircle,
  X,
  XCircle,
} from "@phosphor-icons/react";

// ---------------------------------------------------------------------------
// Eine FESTE, geschlossene Registry — kein `import * as Phosphor`.
//
// Jedes Phosphor-Modul trägt alle sechs Strichstärken als eingebettete
// React-Elemente in EINEM `new Map([...])`-Literal. Weil das ein atomarer
// Ausdruck ist, kann der Bündler die ungenutzten Stärken nicht wegschütteln:
// rund 3,3 KB roh je Symbol, gleich wie viele Stärken wirklich gezeichnet
// werden. Ein Namensraum-Import zöge den ganzen Satz (1 500+ Symbole) ins
// Bündel. Diese Liste hat 87 Symbole und liegt über `manualChunks` in einem
// eigenen Teil (`phosphor`, vite.config.ts), der sich nie ändert.
//
// WER EIN SYMBOL ERGÄNZT, ergänzt es HIER — und nirgends sonst. Die Namen sind
// die Phosphor-Namen in Bindestrichschreibung, damit ein Aufrufer sie aus der
// Dokumentation abschreiben kann.
//
// NICHT hier: die Piktogramme der Ansichten (F4 zeichnet sie in
// `screens/ansichten/ikonen.tsx` selbst als Pfade) — das ist Absicht und
// keine Doppelung: dort sind es handgezeichnete Strichzeichnungen im Stil der
// Entwurfsmappe, hier ist es die Bedienoberfläche.
// ---------------------------------------------------------------------------
const REGISTRY = {
  // Wegweisung
  house: House, // Reiter Start (§32), „Zur Startseite"
  map: MapTrifold, // Reiter Karte
  "squares-four": SquaresFour, // Reiter Projekte
  calendar: CalendarBlank, // Reiter Abgaben
  users: Users, // Reiter Team
  buildings: Buildings, // Reiter Büros
  "bookmark-simple": BookmarkSimple, // Ansicht speichern (033)
  "magnifying-glass": MagnifyingGlass,
  funnel: Funnel, // Filterleiste
  sun: Sun,
  moon: Moon,
  user: User,
  gear: Gear,
  sliders: SlidersHorizontal, // Verwaltung
  "sign-out": SignOut,
  "sign-in": SignIn,
  "dots-three": DotsThree,

  // Karte und Zeichnen
  "map-pin": MapPin,
  polygon: Polygon, // Projektgrenze
  hand: HandGrabbing, // Verschieben / Ziehspiel
  cube: Cube, // 3D-Gebäude
  city: City, // Stadtgrenzen
  mountains: Mountains, // Gelände
  compass: Compass, // nach Norden
  crosshair: Crosshair, // Punkt setzen
  link: LinkSimple, // die Bögen Person → Projekt
  "globe-simple": GlobeSimple, // öffentlich

  // Handwerk
  plus: Plus,
  minus: Minus,
  x: X,
  check: Check,
  trash: Trash,
  copy: Copy,
  "pencil-simple": PencilSimple,
  star: Star, // Titelbild
  "caret-up": CaretUp,
  "caret-down": CaretDown,
  "caret-left": CaretLeft,
  "caret-right": CaretRight,
  "arrow-left": ArrowLeft,
  "arrows-in": ArrowsIn,
  "arrows-out": ArrowsOut,
  "arrow-clockwise": ArrowClockwise,
  down: CaretDown, // Zweitname aus der Entwurfsmappe

  // Dateien und Zeit
  "upload-simple": UploadSimple,
  "download-simple": DownloadSimple,
  image: Image,
  "file-pdf": FilePdf,
  // §53: Videos in Beiträgen — das Abspielzeichen auf Standbild und Platzhalter,
  // die Kamera am Anhang-Chip und im Knopf „Video hinzufügen".
  play: Play,
  "video-camera": VideoCamera,
  camera: Camera,
  clock: Clock,

  // Kontakt (Büroseite)
  mail: Envelope,
  phone: Phone,

  // Erinnerungen (SPEC §11.4): die Glocke am Teameintrag und im Profil.
  // Zwei Symbole und nicht eines mit halber Deckkraft — „stumm" muss man
  // erkennen können, ohne zwei Zeilen nebeneinander zu halten (WCAG 1.4.1).
  bell: Bell,
  "bell-slash": BellSlash,

  // Zweitnamen aus der Entwurfsmappe und aus F4s `ikonen.tsx`. Sie kosten
  // NICHTS — es ist derselbe Verweis, kein zweites Modul —, und sie machen
  // dieselbe Bedienoberfläche unter dem Namen ansprechbar, unter dem sie in
  // .design/lib.mjs gezeichnet ist. Dadurch kann F4 seine handgezeichneten
  // Strichsymbole fallen lassen, ohne jede Zeile umzubenennen.
  chevron: CaretRight,
  pin: MapPin,
  board: SquaresFour,
  building: Buildings,

  // Startseite (§32). Die Zuordnung zu Abzeichen, Serien und Kacheln steht in
  // SPEC §32.10 — dort und nicht hier wird sie geändert, damit Startseite und
  // `/erfolge` dasselbe Zeichen für dasselbe Abzeichen zeigen.
  fire: Fire, // Serien
  "seal-check": SealCheck, // erledigte Wochenaufgabe
  "lock-simple": LockSimple, // „Nur du siehst das."
  "chat-circle": ChatCircle, // Abzeichen stimme
  "hand-heart": HandHeart, // Abzeichen helfer
  "paper-plane-tilt": PaperPlaneTilt, // Abzeichen abgabe
  flag: Flag, // Abzeichen meilenstein
  "rocket-launch": RocketLaunch, // Abzeichen projektstart
  trophy: Trophy, // Abzeichen preis
  sparkle: Sparkle, // Abzeichen anerkennung
  lightbulb: Lightbulb, // Abzeichen ideengeber
  confetti: Confetti, // Abzeichen willkommen
  "user-circle": UserCircle, // Abzeichen steckbrief
  medal: Medal, // Abzeichen jubilaeum
  "dots-six-vertical": DotsSixVertical, // Ziehgriff einer angehefteten Kachel
  "x-circle": XCircle, // Kachel lösen
  "arrow-up-right": ArrowUpRight, // fremder Link, öffnet in neuem Reiter
  timer: Timer, // Phase0: Zeiten erfassen
  "airplane-tilt": AirplaneTilt, // Phase0: Abwesenheiten

  // Zeitung und Links (§34.8). Diese Baustelle trägt ALLE ein, die §34 braucht —
  // auch die der Zeitung —, damit nicht zwei Zweige dieselbe Liste anfassen.
  newspaper: Newspaper, // Reiter Zeitung
  rss: Rss, // Aus der Branche
  "push-pin": PushPin, // an die Startseite heften
  "push-pin-slash": PushPinSlash, // von der Startseite lösen (beim Zeigen auf einen angehefteten)
  folder: FolderSimple, // Ordner unter „Meine Links", zugeklappt
  "folder-open": FolderOpen, // Ordner, aufgeklappt
  "folder-plus": FolderSimplePlus, // Ordner anlegen

  // Galerie im Bildbetrachter (§38.1, E38.7). „Drehen" ist `arrow-clockwise`
  // von oben; `arrow-counter-clockwise` ist „Zurücksetzen" im Zuschnitt und
  // „Rückgängig" nach dem Löschen.
  crop: Crop, // Zuschneiden
  swap: Swap, // Bild ersetzen
  "arrow-counter-clockwise": ArrowCounterClockwise,

  // Projekt-Cockpit (§39.13): die Zeilen der Jetzt-Leiste — die Raute für den
  // Meilenstein (wie im Entwurf), der Pfeil für den nächsten Schritt — und das
  // Kalender-Abo im Profil.
  diamond: Diamond, // Meilenstein
  "arrow-right": ArrowRight, // Nächster Schritt
  "calendar-plus": CalendarPlus, // Kalender abonnieren

  // Termin-Punkte und Projektvorlagen (§50.10): die Sanduhr an einer Klärung, die auf jemanden
  // wartet (Liste, Cockpit-Hinweis), die Liste mit Haken für „Punkte festhalten" und der Stapel
  // für den Reiter „Projektvorlagen" der Verwaltung und die Wahl im Anlegeformular.
  hourglass: HourglassSimple, // Klärung „wartet auf …"
  "list-checks": ListChecks, // Punkte festhalten
  stack: Stack, // Projektvorlagen

  // Zustände und Meldungen
  warning: Warning,
  "warning-circle": WarningCircle,
  "check-circle": CheckCircle,
  info: Info,
  "spinner-gap": SpinnerGap,
} satisfies Record<string, PhosphorIcon>;

/** Alle Namen, die `<Icon name="…">` kennt. */
export type IconName = keyof typeof REGISTRY;
export const ICON_NAMEN = Object.keys(REGISTRY) as IconName[];

export interface IconProps {
  /** Ein Name aus der Registry. Absichtlich `string`: ein Aufrufer soll einen
   *  Namen aus einer Datenbankzeile durchreichen dürfen. */
  name: string;
  /** Strichstärke; Vorgabe "regular". Kräftige Stärken sind Ausnahmen. */
  weight?: IconWeight;
  size?: number;
  className?: string;
  style?: CSSProperties;
  /** wird zum barrierefreien Namen; ohne ihn ist das Symbol reine Zier. */
  title?: string;
}

export function Icon({ name, weight = "regular", size = 18, className = "", style = {}, title }: IconProps) {
  const Cmp = (REGISTRY as Record<string, PhosphorIcon | undefined>)[name];

  if (!Cmp) {
    // Ein vertippter Name fällt nicht beim Übersetzen auf, sondern als leere
    // Stelle im Bild. In der Entwicklung deshalb einmal laut werden; im Bau
    // bleibt ein maßhaltiger, unsichtbarer Platzhalter, damit die Zeile nicht
    // um die Breite des fehlenden Symbols zusammenrutscht.
    if (import.meta.env.DEV) console.warn(`[Icon] Name nicht in der Registry: ${name}`);
    return <span aria-hidden style={{ width: size, height: size, display: "inline-block", verticalAlign: "middle", flex: "none" }} />;
  }

  return (
    <Cmp
      size={size}
      weight={weight}
      className={className}
      style={{ verticalAlign: "middle", flex: "none", ...style }}
      aria-hidden={title ? undefined : true}
      alt={title}
    />
  );
}
